import {computed, effect, inject, Injectable, signal, untracked} from "@angular/core"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {AuthService} from "@common/services/auth/auth.service"
import {BlenderAddonDownloadGQL} from "@common/services/blender-addon/blender-addon.generated"

/*
 * Provides the download link of the latest Blender add-on release,
 * shared by every place that links to it so the links can't drift apart.
 * `$download` is null while loading, when no release is available, and while logged out (the query
 * requires a login), so it is (re)loaded whenever the logged-in user changes.
 */
@Injectable({
    providedIn: "root",
})
export class BlenderAddonService {
    private readonly $writableDownload = signal<{version: string; url: string; fileName: string} | null>(null)
    readonly $download = this.$writableDownload.asReadonly()

    private readonly blenderAddonDownloadGql = inject(BlenderAddonDownloadGQL)
    private readonly auth = inject(AuthService)
    private readonly $userId = computed(() => this.auth.$user()?.id)

    constructor() {
        effect(() => {
            const userId = this.$userId()
            untracked(() => {
                if (userId === undefined) {
                    this.$writableDownload.set(null)
                } else {
                    void this.load()
                }
            })
        })
    }

    private async load() {
        const result = await fetchThrowingErrors(this.blenderAddonDownloadGql)({}).catch(() => null)
        this.$writableDownload.set(result?.blenderAddonDownload ?? null)
    }
}
